import React, { useState, useEffect, useCallback } from "react";
import {
  Flex, Box, Text, SimpleGrid, Select, Icon, Tabs, TabList, TabPanels, Tab, TabPanel,
  Progress, Badge, HStack,
} from "@chakra-ui/react";
import { Headset, History, MessageSquare, Phone, Send, Sparkles, Settings, Users, ListChecks, Contact, Target, Timer } from "lucide-react";
import { getCrmStats, getCrmMinhasMetas } from "../services/api";
import CarteiraTab from "../components/Crm/CarteiraTab";
import BaseTab from "../components/Crm/BaseTab";
import TarefasTab from "../components/Crm/TarefasTab";
import AtividadesTab from "../components/Crm/AtividadesTab";
import ConversasTab from "../components/Crm/ConversasTab";
import OportunidadesTab from "../components/Crm/OportunidadesTab";
import ConfiguracoesTab from "../components/Crm/ConfiguracoesTab";
import ChatModal from "../components/Crm/ChatModal";
import FichaClienteModal from "../components/Crm/FichaClienteModal";

const ACCENT = "#F68633";

const META_ICON = {
  crm_ligacoes_realizadas: Phone,
  crm_mensagens_whatsapp: MessageSquare,
  crm_clientes_contatados: Users,
  crm_tarefas_concluidas: ListChecks,
  crm_tarefas_sla: Timer,
};

// Card de metas do dia (modo CRM). Para o próprio gerente de contas, ou — se o
// usuário logado for ADMIN — para qualquer atendente CRM, via seletor.
function MetasHojeCard({ metas, isAdmin, atendentes, selectedUserId, onChangeUser }) {
  const temSelector = isAdmin && Array.isArray(atendentes) && atendentes.length > 0;
  const temIndicadores = metas && Array.isArray(metas.indicadores) && metas.indicadores.length > 0;
  if (!temIndicadores && !temSelector) return null;
  const nota = metas?.nota_geral;
  const notaCor = nota == null ? "gray" : nota >= 85 ? "green" : nota >= 50 ? "yellow" : "red";
  return (
    <Box bg="white" borderRadius="xl" p={4} mb={4} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
      <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
        <HStack flexWrap="wrap">
          <Icon as={Target} color={ACCENT} boxSize={5} />
          <Text fontWeight="bold" fontSize="md">{isAdmin ? "Metas de hoje" : "Minhas metas de hoje"}</Text>
          {temSelector && (
            <Select size="sm" maxW="220px" bg="white" value={selectedUserId || ""} onChange={(e) => onChangeUser(e.target.value)}>
              {atendentes.map((a) => <option key={a.user_id} value={a.user_id}>{a.nome}</option>)}
            </Select>
          )}
        </HStack>
        {temIndicadores && nota != null && (
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.500">Nota do dia</Text>
            <Badge colorScheme={notaCor} fontSize="sm" borderRadius="md" px={2}>{Number(nota).toFixed(0)}</Badge>
          </HStack>
        )}
      </Flex>
      {!temIndicadores ? (
        <Text fontSize="sm" color="gray.400">Selecione um atendente para ver as metas do dia.</Text>
      ) : (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
        {metas.indicadores.map((ind) => {
          const pct = Math.max(0, Math.min(100, Number(ind.pct ?? 0)));
          const Ico = META_ICON[ind.codigo] || Target;
          const cor = pct >= 100 ? "green" : pct >= 60 ? "teal" : pct >= 30 ? "orange" : "red";
          return (
            <Box key={ind.codigo} borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={3}>
              <HStack spacing={1.5} mb={1} minW={0}>
                <Icon as={Ico} boxSize={4} color="gray.500" />
                <Text fontSize="xs" color="gray.600" noOfLines={1}>{(ind.nome || "").replace(" (CRM)", "")}</Text>
              </HStack>
              <Text fontSize="lg" fontWeight="bold" lineHeight={1}>
                {Math.round(Number(ind.realizado || 0))}
                <Text as="span" fontSize="xs" color="gray.400" fontWeight="normal"> / {ind.meta ?? "—"}</Text>
              </Text>
              <Progress value={pct} size="xs" colorScheme={cor} borderRadius="full" mt={2} />
            </Box>
          );
        })}
      </SimpleGrid>
      )}
    </Box>
  );
}

export default function CRM() {
  const [stats, setStats] = useState(null);
  const [metas, setMetas] = useState(null);
  const [metasIsAdmin, setMetasIsAdmin] = useState(false);
  const [atendentesCrm, setAtendentesCrm] = useState([]);
  const [metasUserId, setMetasUserId] = useState("");
  const [periodo, setPeriodo] = useState("mes");
  const [chatId, setChatId] = useState(null);
  const [fichaId, setFichaId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const carregarStats = useCallback(async () => {
    const r = await getCrmStats();
    if (r?.data) setStats(r.data);
  }, []);

  const carregarMetas = useCallback(async (userId) => {
    const m = await getCrmMinhasMetas(userId ? { user_id: userId } : {});
    if (!m) { setMetas(null); setMetasIsAdmin(false); setAtendentesCrm([]); return; }
    setMetas(m.data || null);
    setMetasIsAdmin(!!m.is_admin);
    setAtendentesCrm(m.atendentes_crm || []);
    if (m.selected_user_id != null) setMetasUserId(String(m.selected_user_id));
  }, []);

  useEffect(() => { carregarStats(); }, [carregarStats, refreshKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarMetas(metasUserId || undefined); }, [carregarMetas, refreshKey]);

  const abrirChat = (id) => setChatId(id);
  const fecharChat = () => { setChatId(null); setRefreshKey((k) => k + 1); carregarStats(); carregarMetas(metasUserId || undefined); };
  const abrirFicha = (id) => setFichaId(id);
  const fecharFicha = () => { setFichaId(null); setRefreshKey((k) => k + 1); };

  const p = stats?.[periodo] || {};
  const cards = [
    { label: "Contatos no chat", valor: p.chat_contatos, icon: MessageSquare, cor: "green.500" },
    { label: "Mensagens enviadas", valor: p.chat_mensagens, icon: Send, cor: "blue.500" },
    { label: "Contatos ligados", valor: p.ligacoes_contatos, icon: Phone, cor: "purple.500" },
    { label: "Ligações realizadas", valor: p.ligacoes_total, icon: Phone, cor: ACCENT },
  ];

  return (
    <>
      <Flex justify="center" align="start" bg="gray.50" minH="100vh" p={{ base: 2, md: 4 }}>
        <Box w={{ base: "100%", lg: "95%", xl: "90%" }} maxW="1800px">
          {/* Cabeçalho */}
          <Flex justify="space-between" align="center" my={4} flexWrap="wrap" gap={3}>
            <Flex align="center" gap={2}>
              <Icon as={Headset} color={ACCENT} boxSize={6} />
              <Text color={ACCENT} fontWeight="bold" fontSize="2xl">CRM</Text>
            </Flex>
            <Select w="170px" size="sm" bg="white" borderRadius="md" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              <option value="hoje">Hoje</option>
              <option value="semana">Esta semana</option>
              <option value="mes">Este mês</option>
            </Select>
          </Flex>

          {/* Métricas */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
            {cards.map((c, i) => (
              <Box key={i} bg="white" borderRadius="xl" p={4} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                <Flex align="center" gap={3}>
                  <Flex w={10} h={10} borderRadius="lg" bg="gray.50" align="center" justify="center" flexShrink={0}>
                    <Icon as={c.icon} color={c.cor} boxSize={5} />
                  </Flex>
                  <Box minW={0}>
                    <Text fontSize="2xl" fontWeight="bold" lineHeight={1}>{c.valor ?? 0}</Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.label}</Text>
                  </Box>
                </Flex>
              </Box>
            ))}
          </SimpleGrid>

          {/* Metas do dia (gerente de contas; admin vê de qualquer atendente CRM) */}
          <MetasHojeCard
            metas={metas}
            isAdmin={metasIsAdmin}
            atendentes={atendentesCrm}
            selectedUserId={metasUserId}
            onChangeUser={(v) => { setMetasUserId(v); carregarMetas(v || undefined); }}
          />

          {/* Abas */}
          <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
            <Tabs colorScheme="orange" isLazy>
              <TabList px={{ base: 2, md: 4 }} pt={2} overflowX="auto">
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={Users} boxSize={4} mr={2} />Minha carteira</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={Contact} boxSize={4} mr={2} />Base de contatos</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={ListChecks} boxSize={4} mr={2} />Tarefas</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={History} boxSize={4} mr={2} />Atividades</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={MessageSquare} boxSize={4} mr={2} />Conversas</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={Sparkles} boxSize={4} mr={2} />Oportunidades</Tab>
                <Tab fontWeight="semibold" fontSize="sm"><Icon as={Settings} boxSize={4} mr={2} />Configurações</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <CarteiraTab onAbrirChat={abrirChat} onAbrirFicha={abrirFicha} refreshKey={refreshKey} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <BaseTab onAbrirChat={abrirChat} onAbrirFicha={abrirFicha} refreshKey={refreshKey} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <TarefasTab onAbrirChat={abrirChat} onAbrirFicha={abrirFicha} refreshKey={refreshKey} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <AtividadesTab onAbrirFicha={abrirFicha} refreshKey={refreshKey} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <ConversasTab onAbrirChat={abrirChat} refreshKey={refreshKey} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <OportunidadesTab onAbrirChat={abrirChat} onAbrirFicha={abrirFicha} />
                </TabPanel>
                <TabPanel px={{ base: 2, md: 4 }}>
                  <ConfiguracoesTab />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Flex>

      <ChatModal contatoId={chatId} isOpen={!!chatId} onClose={fecharChat} />
      <FichaClienteModal
        clienteId={fichaId}
        isOpen={!!fichaId}
        onClose={fecharFicha}
        onAbrirChat={abrirChat}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
    </>
  );
}
